import { QueryClient, useQueryClient } from "@tanstack/react-query";
import { useMemo } from "react";
import { InventoryAPIPromiseClient } from "../../gen/witbooking/backoffice/inventory/v1/inventory_api_grpc_web_pb";
import {
  GetInventoryLinesRequest,
  GetInventoryLinesResponse,
  GetInventoryStateRequest,
  InventoryLine,
  InventoryState,
} from "../../gen/witbooking/backoffice/inventory/v1/inventory_api_pb";
import {
  GetAllOccupationsRequest,
  GetAllOccupationsResponse,
} from "../../gen/witbooking/backoffice/occupation/v1/occupation_api_pb";
import { AuthorizationService, useAuthorizationService } from "../../lib/auth.service";
import { getLanguageFromFullLocale } from "../../lib/config";
import { PropertyScopedGrpcCall, useGrpcClient, usePropertyScopedGrpcCall } from "../../lib/grpc";
import { InventoryEntry, VirtualInventoryLine } from "../../lib/model";
import { accommodationQueryKeys } from "../accommodation/accommodation.queryKeys";
import { useAccommodationApi } from "../accommodation/infrastructure/api/accommodation.api";
import { OccupationEntity } from "../occupation/domain/occupation.entity";
import { occupationsQueryKeys } from "../occupation/infrastructure/cache/OccupationsCacheStrategy";
import {
  occupationClient,
  toOccupationEntities,
} from "../occupation/infrastructure/occupation.api";
import { ConditionService } from "../witadmin/CRUD/Conditions/application/condition.service";
import { Condition } from "../witadmin/CRUD/Conditions/domain/Condition.entity";
import { useConditionService } from "../witadmin/CRUD/Conditions/hook/useConditionService";

export class InventoryService {
  constructor(
    private readonly inventoryClient: InventoryAPIPromiseClient,
    private readonly authorizationService: AuthorizationService,
    private readonly conditionService: ConditionService,
    private readonly accommodationService: ReturnType<typeof useAccommodationApi>,
    private readonly queryClient: QueryClient,
    private readonly getInventoryLinesCall: PropertyScopedGrpcCall<
      GetInventoryLinesRequest,
      GetInventoryLinesResponse
    >,
    private readonly getAllOccupationsCall: PropertyScopedGrpcCall<
      GetAllOccupationsRequest,
      GetAllOccupationsResponse
    >
  ) {}

  async getInventoryEntries(propertyTicker: string, locale: string): Promise<InventoryEntry[]> {
    const conditions = await this.conditionService.getAll(propertyTicker);
    const occupations = await this.queryClient.ensureQueryData({
      queryKey: occupationsQueryKeys.list(propertyTicker),
      queryFn: async () => {
        const response = await this.getAllOccupationsCall(
          propertyTicker,
          new GetAllOccupationsRequest()
        );
        return toOccupationEntities(response);
      },
      staleTime: 5 * 60 * 1000,
    });

    const inventoryRequest = new GetInventoryLinesRequest();
    inventoryRequest.setLocale(getLanguageFromFullLocale(locale));
    inventoryRequest.setOnlyValidLines(true);

    const accommodations = await this.queryClient.ensureQueryData({
      queryKey: accommodationQueryKeys.list(propertyTicker),
      queryFn: () => this.accommodationService.getAccommodations(propertyTicker),
      staleTime: 5 * 60 * 1000,
    });
    const combinationList = accommodations.reduce(
      (previousValue, currentValue) => ({
        ...previousValue,
        [currentValue.ticker]: currentValue.occupancyCombinationsList || [],
      }),
      {} as Record<string, string[]>
    );

    const inventoryResponse = await this.getInventoryLinesCall(propertyTicker, inventoryRequest);
    return this.toInventoryEntries(
      inventoryResponse.getInventoryLinesList(),
      conditions,
      locale,
      combinationList,
      occupations
    );
  }

  async getCurrentInventoryState(propertyTicker: string): Promise<InventoryState[]> {
    const token = await this.authorizationService.getRptToken(propertyTicker);
    const response = await this.inventoryClient.getCurrentInventoryState(
      new GetInventoryStateRequest(),
      {
        Authorization: `Bearer ${token}`,
        "x-property": propertyTicker,
      }
    );
    return response.getInventoriesList();
  }

  private generateVirtualInventoryTickers(
    inventoryLine: InventoryLine,
    combinationList: Record<string, string[]>,
    occupations: OccupationEntity[]
  ): VirtualInventoryLine[] {
    const accommodationTicker = inventoryLine.getAccommodation()!.getTicker();
    const conditionTicker = inventoryLine.getCondition()!.getTicker();
    const mealPlanTicker = inventoryLine.getMealPlan()!.getTicker();

    const combinations = combinationList[accommodationTicker];
    if (!combinations || combinations.length === 0) {
      return [];
    }

    return combinations.map(occupancyTicker => {
      const occupation = occupations.find(({ ticker }) => ticker === occupancyTicker);
      const translations =
        occupation?.translationsList.reduce(
          (acc, { locale, name }) => ({
            ...acc,
            [locale]: { name },
          }),
          {} as { [locale: string]: { name: string } }
        ) || {};

      return {
        ticker: `${accommodationTicker}_${occupancyTicker}_${mealPlanTicker}_${conditionTicker}`,
        translations,
      };
    });
  }

  private toInventoryEntries(
    inventories: InventoryLine[],
    conditions: Condition[],
    locale: string,
    combinationList: Record<string, string[]>,
    occupations: OccupationEntity[]
  ): InventoryEntry[] {
    const conditionsByTicker = conditions.reduce(
      (acc, condition) => ({
        ...acc,
        [condition.ticker]: condition,
      }),
      {} as Record<string, Condition>
    );

    return inventories.map(v => {
      const condition = conditionsByTicker[v.getCondition()!.getTicker()];
      return {
        accommodation: v.getAccommodation()!.toObject(),
        condition: {
          name: condition.getTranslation(locale).name,
          ticker: condition.ticker,
          type: condition.conditionType,
        },
        configuration: v.getConfiguration()!.toObject(),
        mealPlan: v.getMealPlan()!.toObject(),
        ticker: v.getCode(),
        legacyId: v.getLegacyId(),
        blockingType: v.getBlockingType(),
        virtualInventory: this.generateVirtualInventoryTickers(v, combinationList, occupations),
      };
    });
  }
}

export const useInventoryService = (): InventoryService => {
  const inventoryClient = useGrpcClient(InventoryAPIPromiseClient);
  const authService = useAuthorizationService();
  const conditionService = useConditionService();
  const accommodationService = useAccommodationApi();
  const queryClient = useQueryClient();
  const getInventoryLinesCall = usePropertyScopedGrpcCall(
    inventoryClient,
    inventoryClient.getInventoryLines
  );
  const getAllOccupationsCall = usePropertyScopedGrpcCall(
    occupationClient,
    occupationClient.getAllOccupations
  );

  return useMemo(() => {
    return new InventoryService(
      inventoryClient,
      authService,
      conditionService,
      accommodationService,
      queryClient,
      getInventoryLinesCall,
      getAllOccupationsCall
    );
  }, [
    authService,
    conditionService,
    inventoryClient,
    accommodationService,
    queryClient,
    getInventoryLinesCall,
    getAllOccupationsCall,
  ]);
};
